import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import usePermission from '@/hooks/usePermission';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { DashboardContent } from '@/layouts/dashboard';
import { DottedTrail } from '@/pages/dashboard/home/hero-welcome';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaFeed from '@/assets/images/tia/TIA-FEED.png';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Page, PageContent, PageSidebar } from '@/components/custom/page';
import { Filter, FilterContent, FilterCombobox } from '@/components/custom/filter';
import { PostShare } from './PostShare';
import { PostCard } from './PostCard';
import { ProfessorsSection } from './ProfessorsSection';

const FEED_WELCOME_MESSAGE_STUDENT = 'Compartilhe conquistas e tire dúvidas com a turma. Aqui a gente cresce junto! 🚀';
const FEED_WELCOME_MESSAGE_TEACHER = 'Acompanhe as conquistas e dúvidas da turma e fixe os avisos mais importantes por aqui! 📌';

const FEED_TIA_DECORATION = (
  <>
    <DottedTrail />
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-5 top-2 text-xl"
    >
      ⭐
    </span>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-12 top-6 text-lg"
      style={{ transform: 'rotate(-20deg)' }}
    >
      ✈️
    </span>
  </>
);

function FeedPlayful() {
  const { t } = useTranslation();
  const client = useClient();
  const isPlayfulTeacherTheme = usePlayfulTeacher();

  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [posts, setPosts] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [isLoadingClassrooms, setIsLoadingClassrooms] = useState(false);
  const [isLoadingProfessors, setIsLoadingProfessors] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState('blank');

  const getPostsList = async () => {
    setIsLoadingPosts(true);
    setHasMore(true);
    setPageNumber(1);
    const classroomId = selectedClassroomId === 'blank' ? null : selectedClassroomId;
    try {
      const { data } = await client.fetch(`posts?page[number]=1&page[size]=10${classroomId ? `&filter[classroom_id]=${classroomId}` : ''}`);
      if (data.length === 0) {
        setHasMore(false);
      }
      setPosts([...data]);
    } catch (e) {
      toast.error(t('toast.errorGetPost'));
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const updatePostsList = async () => {
    const classroomId = selectedClassroomId === 'blank' ? null : selectedClassroomId;

    try {
      const { data } = await client.fetch(`posts?page[number]=${pageNumber}&page[size]=10${classroomId ? `&filter[classroom_id]=${classroomId}` : ''}`);

      if (data.length === 0) {
        setHasMore(false);
      }

      setPosts(prev => [...prev, ...data]);
    } catch (e) {
      toast.error(t('toast.errorGetPost'));
    }
  };

  const getClassrooms = async () => {
    setIsLoadingClassrooms(true);
    const { error, data } = await client.fetch('classrooms');

    if (error) {
      toast.error(t('toast.errorGetClassroom'));
    } else {
      setClassrooms(data);
    }
    setIsLoadingClassrooms(false);
  };

  const getProfessors = async () => {
    setIsLoadingProfessors(true);
    const { error, data } = await client.fetch('users?filter[profile]=Professor');

    if (error) {
      toast.error(t('toast.errorGetClassroom'));
    } else {
      setProfessors(data);
    }
    setIsLoadingProfessors(false);
  };

  useEffect(() => {
    getClassrooms();
    getProfessors();
  }, []);

  useEffect(() => {
    getPostsList();
  }, [selectedClassroomId]);

  useEffect(() => {
    if (pageNumber !== 1) {
      updatePostsList();
    }
  }, [pageNumber]);

  const addPost = post => setPosts(prev => [post, ...prev]);

  const classroomOptions = React.useMemo(
    () => [
      { value: 'blank', label: 'Todas' },
      ...(classrooms?.map(c => ({
        value: c?.id,
        label: c?.institution?.name + '-' + c?.course?.title + '-' + c?.title
      })) || [])
    ],
    [classrooms]
  );

  const breadcrumbs = {
    title: 'Comunidade',
    nav: [{ route: '/feed', name: 'Fórum', isActive: true }]
  };

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      <div className="flex flex-col gap-4">
        <TiaBanner
          image={isPlayfulTeacherTheme ? JOINHA : tiaFeed}
          imageAlt={isPlayfulTeacherTheme ? 'Personagem do professor' : undefined}
          imageHeight={170}
          imageOffset={200}
          bubbleCorner="tl"
          bubbleMaxWidth={400}
          text={isPlayfulTeacherTheme ? FEED_WELCOME_MESSAGE_TEACHER : FEED_WELCOME_MESSAGE_STUDENT}
          decoration={FEED_TIA_DECORATION}
          className="sm:mt-10"
        />

        <Page className="gap-y-6 lg:gap-y-[60px]">
          <PageContent>
            <Filter className="mb-0">
              <FilterContent>
                <FilterCombobox
                  label={t('filter.labelSelectTheClassroom')}
                  value={selectedClassroomId}
                  onValueChange={setSelectedClassroomId}
                  options={classroomOptions}
                  loading={isLoadingClassrooms}
                />
              </FilterContent>
            </Filter>

            {usePermission('Criar postagens e comentários', 'Fórum') && (
              <PostShare
                selectedClassroomId={selectedClassroomId}
                onPostCreated={addPost}
              />
            )}

            {isLoadingPosts && <Loader />}

            {!isLoadingPosts && posts.length === 0 && (
              <EmptyState
                type="content"
                variant="plain"
                title="Nenhuma postagem por aqui"
                text="Ainda não há postagens para a turma selecionada."
                className="pt-8"
              />
            )}

            {!isLoadingPosts && posts.length > 0 && (
              <InfiniteScroll
                dataLength={posts.length}
                next={() => setPageNumber(pageNumber + 1)}
                hasMore={hasMore}
                loader={<Loader />}
                className="-m-3 flex flex-col gap-4 p-3"
              >
                {posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    setPosts={setPosts}
                  />
                ))}
              </InfiniteScroll>
            )}
          </PageContent>

          <PageSidebar forum>
            <ProfessorsSection
              professors={professors}
              loading={isLoadingProfessors}
            />
          </PageSidebar>
        </Page>
      </div>
    </DashboardContent>
  );
}

export default FeedPlayful;
